import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  LayoutDashboard,
  GraduationCap,
  ClipboardCheck,
  BookOpen,
  ClipboardList,
  FileText,
  Users,
  Settings,
  LogOut,
  Briefcase,
  TrendingUp,
  Shield,
} from 'lucide-react';
import { Button } from '../ui/button';

// Stryde Research Logo URLs
const STRYDE_LOGO = "https://customer-assets.emergentagent.com/job_onboard-mastery/artifacts/2d1ii1ii_stryde-fin-01.jpg";
const STRYDE_ICON = "https://customer-assets.emergentagent.com/job_onboard-mastery/artifacts/x579wpam_stryde-fin-02.jpg";

const Sidebar = () => {
  const { user, logout, canManage, isAdmin } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Employee-facing items at top, grouped logically
  const employeeItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/onboarding', icon: ClipboardCheck, label: 'My Onboarding' },
    { to: '/assignments', icon: ClipboardList, label: 'My Assignments' },
    { to: '/certifications', icon: Shield, label: 'My Certifications' },
    { to: '/reports', icon: FileText, label: 'My Reports' },
  ];

  // Resource library items
  const resourceItems = [
    { to: '/trainings', icon: GraduationCap, label: 'Training Library' },
    { to: '/sops', icon: BookOpen, label: 'SOP Library' },
  ];

  // Admin/Lead management items
  const adminItems = [];
  if (canManage) {
    adminItems.push(
      { to: '/curricula', icon: Briefcase, label: 'Curricula' },
      { to: '/onboarding-dashboard', icon: TrendingUp, label: 'Team Progress' }
    );
  }
  if (isAdmin) {
    adminItems.push({ to: '/users', icon: Users, label: 'Users' });
  }

  const renderNavSection = (items, label) => (
    <>
      {label && (
        <p className="px-4 pt-4 pb-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">
          {label}
        </p>
      )}
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200
            ${isActive 
              ? 'bg-blue-50 text-blue-700 shadow-sm' 
              : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
            }`
          }
        >
          <item.icon className="w-5 h-5" strokeWidth={1.5} />
          {item.label}
        </NavLink>
      ))}
    </>
  );

  return (
    <aside className="w-64 bg-white border-r border-slate-200 flex flex-col h-screen" data-testid="sidebar">
      {/* Stryde Logo */}
      <div className="p-5 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <img 
            src={STRYDE_ICON} 
            alt="Stryde" 
            className="w-10 h-10 object-contain"
          />
          <div>
            <h1 className="font-heading font-bold text-lg text-blue-600">Stryde<span className="text-amber-500">Mind</span></h1>
            <p className="text-xs text-slate-500">Learning Management</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {renderNavSection(employeeItems)}
        {renderNavSection(resourceItems, 'Resources')}
        {adminItems.length > 0 && renderNavSection(adminItems, 'Management')}
      </nav>

      {/* User Section */}
      <div className="p-4 border-t border-slate-100">
        <div className="flex items-center gap-3 px-3 py-2 mb-3">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold">
            {user?.name?.charAt(0).toUpperCase() || 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
            <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
            {user?.job_role && (
              <p className="text-xs text-blue-600">{user?.job_role}</p>
            )}
          </div>
        </div>
        
        <div className="space-y-1">
          <NavLink
            to="/settings"
            className="flex items-center gap-3 px-4 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors"
          >
            <Settings className="w-4 h-4" />
            Settings
          </NavLink>
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start gap-3 px-4 py-2 text-slate-600 hover:bg-red-50 hover:text-red-600"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </Button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
